/**
 * Tiny GIS utilities that don't deserve a Turf dependency.
 */

// Imported explicitly rather than leaning on the `GeoJSON` UMD global. That
// global is only visible when something else in the dependency tree happens to
// pull `geojson` into the program (it used to arrive via @types/supercluster),
// so a routine transitive-dependency change could break the build.
import type { Geometry } from 'geojson'

/**
 * Normalize a coordinate pair so the longitude wraps cleanly into
 * `[-180, 180]` and the latitude clamps into `[-90, 90]`.
 *
 * MapLibre's default cooperative-gestures behavior lets the user pan
 * continuously across the antimeridian (lon < -180 or lon > 180) and
 * occasionally past the Mercator poles. The API rejects out-of-range
 * coordinates as a 400, so any UI surface that takes a raw map click /
 * drag / paste needs to wrap the value first — same earth point, just
 * the canonical representation of it.
 *
 *   normalizeCoord(-189.8, 12)  → (170.2, 12)    // wrapped two hops east
 *   normalizeCoord(540, 0)      → (-180, 0)      // 540 mod 360 → 180 → -180
 *   normalizeCoord(0, 95)       → (0, 90)        // lat clamped
 */
export function normalizeCoord(lon: number, lat: number): [number, number] {
  // Short-circuit when the value is already canonical — the mod
  // arithmetic below introduces floating-point round-off otherwise
  // (e.g. -110.9747 → -110.97469999999998). For in-range coords we
  // want byte-identical passthrough.
  if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
    return [lon, lat]
  }
  const wrappedLon = (((lon + 180) % 360) + 360) % 360 - 180
  const clampedLat = Math.max(-90, Math.min(90, lat))
  return [wrappedLon, clampedLat]
}

/**
 * Compute the lon/lat bounding box of a GeoJSON Polygon or MultiPolygon.
 * Returns `[minLon, minLat, maxLon, maxLat]`, or `null` for unsupported
 * geometry types or empty coordinates.
 */
export function geomBounds(g: Geometry): [number, number, number, number] | null {
  let minLon = Infinity
  let minLat = Infinity
  let maxLon = -Infinity
  let maxLat = -Infinity

  const visit = (lon: number, lat: number) => {
    if (lon < minLon) minLon = lon
    if (lat < minLat) minLat = lat
    if (lon > maxLon) maxLon = lon
    if (lat > maxLat) maxLat = lat
  }

  if (g.type === 'Polygon') {
    for (const ring of g.coordinates) {
      for (const [lon, lat] of ring) visit(lon, lat)
    }
  } else if (g.type === 'MultiPolygon') {
    for (const poly of g.coordinates) {
      for (const ring of poly) {
        for (const [lon, lat] of ring) visit(lon, lat)
      }
    }
  } else {
    return null
  }

  if (!Number.isFinite(minLon)) return null
  return [minLon, minLat, maxLon, maxLat]
}
